import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { 
  Firestore, 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs,
  doc,
  getDoc
} from '@angular/fire/firestore';
import { AuthService } from '../../services/auth.service';
import { LanguageService } from '../../services/language.service';
import { ProductLocalizationService } from '../../services/product-localization.service';
import { CategoryLocalizationService } from '../../services/category-localization.service';
import { ToastService } from '../../services/toast.service';
import { ShiprocketService, TrackShipmentResponse } from '../../services/shiprocket.service';
import { DoseColorService } from '../../services/dose-color.service';

export interface Order {
  id: string;
  userId: string;
  products: any[];
  totalAmount: number;
  discountAmount?: number;
  deliveryCharges?: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  orderDate: any;
  confirmedDate?: any;
  shippingDate?: any;
  deliveredDate?: any;
  deliveryAddress: string;
  paymentMethod: string;
  paymentStatus: 'pending' | 'completed' | 'failed';
  doseTypes: string[];
  isComboOrder: boolean;
  isOfflinePurchase?: boolean;
  
  // UPI Payment Details
  upiTransactionId?: string;
  upiId?: string;
  upiOrderId?: string;
  
  // Shipping & Tracking Details
  trackingNumber?: string;
  shiprocketAwbCode?: string;
  shiprocketCourierName?: string;
  shippingProvider?: string;
}

@Component({
  selector: 'app-order-history',
  templateUrl: './order-history.html',
  styleUrls: ['./order-history.scss'],
  imports: [CommonModule],
  standalone: true
})
export class OrderHistory implements OnInit {
  orders: Order[] = [];
  loading = true;
  error = false;
  currentUser: any;
  trackingLoading: { [orderId: string]: boolean } = {};
  trackingData: { [orderId: string]: any } = {};
  showTrackingModal: boolean = false;
  selectedOrderForTracking: Order | null = null;

  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private router: Router,
    private languageService: LanguageService,
    public productLocalizationService: ProductLocalizationService,
    private categoryLocalizationService: CategoryLocalizationService,
    private toastService: ToastService,
    private shiprocketService: ShiprocketService,
    private doseColorService: DoseColorService
  ) {}

  ngOnInit() {
    this.loadCurrentUser();
  }

  private loadCurrentUser() {
    this.currentUser = this.authService.getCurrentUser();
    if (this.currentUser) {
      this.loadOrders();
    } else {
      this.error = true;
      this.loading = false;
      this.toastService.error('User not found');
    }
  }

  private async loadOrders() {
    try {
      this.loading = true;
      this.error = false;

      const ordersRef = collection(this.firestore, 'orderTable');
      const q = query(
        ordersRef,
        where('userId', '==', this.currentUser.id),
        orderBy('orderDate', 'desc')
      );

      const querySnapshot = await getDocs(q);
      
      const rawOrders = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        };
      }) as Order[];

      // Enrich products with i18n data from database
      for (const order of rawOrders) {
        if (order.products && Array.isArray(order.products)) {
          order.products = await Promise.all(
            order.products.map(async (product) => {
              // Preserve original image before enrichment
              const originalImage = product.image;
              
              // Enrich with i18n data
              const enrichedProduct = await this.productLocalizationService.enrichProductWithI18n(product);
              
              // Ensure image is preserved (enrichedProduct might not have image if i18n already exists)
              if (!enrichedProduct.image && originalImage) {
                enrichedProduct.image = originalImage;
              }
              
              return enrichedProduct;
            })
          );
        }
      }

      this.orders = rawOrders;
      
      // Load tracking data for orders with tracking numbers
      for (const order of this.orders) {
        if (this.getTrackingNumber(order)) {
          await this.loadTrackingDataForOrder(order);
        }
      }
      
      this.loading = false;
    } catch (error) {
      this.error = true;
      this.loading = false;
      this.toastService.error('Failed to load orders: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'confirmed': return 'status-confirmed';
      case 'shipped': return 'status-shipped';
      case 'delivered': return 'status-delivered';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-pending';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'pending': return this.t('orderStatusPending');
      case 'confirmed': return this.t('orderStatusConfirmed');
      case 'shipped': return this.t('orderStatusShipped');
      case 'delivered': return this.t('orderStatusDelivered');
      case 'cancelled': return this.t('orderStatusCancelled');
      default: return status;
    }
  }

  getPaymentStatusClass(status: string): string {
    switch (status) {
      case 'pending': return 'payment-pending';
      case 'completed': return 'payment-completed';
      case 'failed': return 'payment-failed';
      default: return 'payment-pending';
    }
  }

  getPaymentStatusText(status: string): string {
    switch (status) {
      case 'pending': return this.t('paymentStatusPending');
      case 'completed': return this.t('paymentStatusCompleted');
      case 'failed': return this.t('paymentStatusFailed');
      default: return status;
    }
  }

  formatDate(date: any): string {
    if (!date) return '';
    
    try {
      let dateObj: Date;
      
      if (date.seconds) {
        // Firestore Timestamp
        dateObj = new Date(date.seconds * 1000);
      } else if (date.toDate && typeof date.toDate === 'function') {
        // Firestore Timestamp object
        dateObj = date.toDate();
      } else if (typeof date === 'string') {
        // ISO string or other string formats
        dateObj = new Date(date);
      } else {
        dateObj = new Date(date);
      }
      
      if (isNaN(dateObj.getTime())) {
        return '';
      }
      
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  }

  formatDateTime(date: any): string {
    if (!date) return '';
    
    try {
      const dateObj = date.seconds ? new Date(date.seconds * 1000) : (date.toDate ? date.toDate() : new Date(date));
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  }

  getOrderTotal(order: Order): number {
    return order.totalAmount || 0;
  }

  getProductAmount(order: Order): number {
    return order.totalAmount || 0;
  }

  getDeliveryCharges(order: Order): number {
    return order.deliveryCharges || 0;
  }

  getGrandTotal(order: Order): number {
    const productAmount = this.getProductAmount(order);
    const deliveryCharges = this.getDeliveryCharges(order);
    const discountAmount = order.discountAmount || 0;
    return productAmount + deliveryCharges - discountAmount;
  }

  getOrderProgressSteps(order: Order): Array<{status: string, label: string, completed: boolean, active: boolean, dateTime?: string}> {
    const steps = [
      { status: 'pending', label: 'Order Placed', completed: false, active: false, dateTime: this.formatDate(order.orderDate) },
      { status: 'confirmed', label: 'Confirmed', completed: false, active: false, dateTime: order.confirmedDate ? this.formatDate(order.confirmedDate) : undefined },
      { status: 'shipped', label: 'Shipped', completed: false, active: false, dateTime: order.shippingDate ? this.formatDate(order.shippingDate) : undefined },
      { status: 'delivered', label: 'Delivered', completed: false, active: false, dateTime: order.deliveredDate ? this.formatDate(order.deliveredDate) : undefined }
    ];

    const statusOrder = ['pending', 'confirmed', 'shipped', 'delivered'];
    const currentIndex = statusOrder.indexOf(order.status);

    // Get tracking data if available
    const tracking = this.trackingData[order.id];
    
    // Enhance with Shiprocket tracking data if available
    if (tracking && tracking.tracking_events && Array.isArray(tracking.tracking_events)) {
      // Sort events by date (most recent first, then reverse to get chronological order)
      const sortedEvents = [...tracking.tracking_events].sort((a, b) => {
        const dateA = a.scan_date_time ? new Date(a.scan_date_time).getTime() : 0;
        const dateB = b.scan_date_time ? new Date(b.scan_date_time).getTime() : 0;
        return dateA - dateB; // Chronological order
      });

      sortedEvents.forEach((event: any) => {
        const eventStatus = event.status?.toLowerCase() || '';
        const eventDate = event.scan_date_time ? this.formatTrackingDate(event.scan_date_time) : undefined;
        
        // Map Shiprocket events to our steps - use most recent event for each status
        if (eventStatus.includes('pickup') || eventStatus.includes('picked up') || 
            eventStatus.includes('confirmed') || eventStatus.includes('ready to ship')) {
          const step = steps.find(s => s.status === 'confirmed');
          if (step && eventDate) {
            step.dateTime = eventDate; // Use most recent event
          }
        } else if (eventStatus.includes('shipped') || eventStatus.includes('dispatched') || 
                   eventStatus.includes('in transit') || eventStatus.includes('out for delivery') ||
                   eventStatus.includes('in_transit') || eventStatus.includes('out_for_delivery')) {
          const step = steps.find(s => s.status === 'shipped');
          if (step && eventDate) {
            step.dateTime = eventDate; // Use most recent event
          }
        } else if (eventStatus.includes('delivered') || eventStatus.includes('delivery')) {
          const step = steps.find(s => s.status === 'delivered');
          if (step && eventDate) {
            step.dateTime = eventDate; // Use most recent event
          }
        }
      });
    }

    return steps.map((step, index) => {
      const stepIndex = statusOrder.indexOf(step.status);
      return {
        ...step,
        completed: stepIndex < currentIndex,
        active: stepIndex === currentIndex
      };
    });
  }

  formatTrackingDate(date: any): string {
    if (!date) return '';
    
    try {
      let dateObj: Date;
      
      if (typeof date === 'string') {
        // Handle ISO string or other string formats
        dateObj = new Date(date);
      } else if (date.seconds) {
        // Firestore Timestamp
        dateObj = new Date(date.seconds * 1000);
      } else if (date.toDate && typeof date.toDate === 'function') {
        // Firestore Timestamp object
        dateObj = date.toDate();
      } else {
        dateObj = new Date(date);
      }
      
      if (isNaN(dateObj.getTime())) {
        return '';
      }
      
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  }

  async loadTrackingDataForOrder(order: Order): Promise<void> {
    const trackingNumber = this.getTrackingNumber(order);
    if (!trackingNumber || this.trackingData[order.id]) {
      return; // Already loaded or no tracking number
    }

    try {
      const result = await this.shiprocketService.trackShipment(trackingNumber).toPromise();
      if (result && result.tracking_data) {
        this.trackingData[order.id] = result.tracking_data;
      }
    } catch (error) {
      // Silently fail - tracking data is optional
      console.log('Could not load tracking data for order:', order.id);
    }
  }


  viewOrderDetails(orderId: string) {
    this.router.navigate(['/order', orderId]);
  }

  goBack() {
    this.router.navigate(['/home']);
  }

  retry() {
    this.loadOrders();
  }

  t(key: string, params?: any): string {
    return this.languageService.translate(key, params);
  }

  // Localized product names for order summary
  getProductNames(order: Order): string {
    if (!order.products || order.products.length === 0) return '';
    return order.products.map((product: any) => 
      this.productLocalizationService.getLocalizedName(product)
    ).join(', ');
  }

  // Get display name with category (Product Name + Category Name + Option X)
  getDisplayNameWithCategory(product: any): string {
    const productName = this.productLocalizationService.getLocalizedName(product);
    const categoryName = product.selectedCategoryName;
    
    if (categoryName) {
      // Capitalize first letter of each word in category name
      const formattedCategoryName = categoryName
        .split(' ')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      
      // Add option label if sub-product option is selected
      if (product.selectedSubProductIndex !== undefined && product.selectedSubProductIndex >= 0) {
        const optionLabel = this.getOptionLabel(product.selectedSubProductIndex);
        return `${productName} (${formattedCategoryName}, ${optionLabel})`;
      }
      
      return `${productName} (${formattedCategoryName})`;
    }
    return productName;
  }

  // Get option label (Option A, Option B, etc.)
  getOptionLabel(index: number): string {
    return `Option ${String.fromCharCode(65 + index)}`;
  }

  // Get sub-products for display (items in box) - show selected option text
  // Note: Sub-products are saved as localized strings at order time
  // They will display in the language that was active when the order was placed
  getSubProducts(product: any): string {
    // If selected sub-product text exists, use it (this is the actual data from DB)
    if (product.selectedSubProductText) {
      return product.selectedSubProductText;
    }
    
    // Fallback to array of sub-products (backward compatibility)
    if (product.selectedCategorySubProducts) {
      // Check if it's an array
      if (Array.isArray(product.selectedCategorySubProducts) && product.selectedCategorySubProducts.length > 0) {
        return product.selectedCategorySubProducts.join(', ');
      }
      // If it's a string, return it directly
      if (typeof product.selectedCategorySubProducts === 'string' && product.selectedCategorySubProducts.length > 0) {
        return product.selectedCategorySubProducts;
      }
    }
    return '';
  }

  // Get product image URL
  getProductImage(product: any): string {
    if (!product) {
      return 'https://placehold.co/60x60/9E9E9E/white?text=Product';
    }
    
    try {
      // Helper function to convert image to array (handles array, object with numeric keys, or string)
      const normalizeImageToArray = (img: any): string[] => {
        if (!img) return [];
        
        // If it's already an array
        if (Array.isArray(img)) {
          return img.filter(url => typeof url === 'string' && url.trim().length > 0);
        }
        
        // If it's a string
        if (typeof img === 'string' && img.trim().length > 0) {
          return [img.trim()];
        }
        
        // If it's an object (Firestore sometimes stores arrays as objects with numeric keys)
        if (typeof img === 'object' && img !== null) {
          // Check if it has numeric keys (like {0: 'url1', 1: 'url2'})
          const keys = Object.keys(img);
          const numericKeys = keys.filter(k => !isNaN(Number(k)));
          
          if (numericKeys.length > 0) {
            // Convert object with numeric keys to array
            return numericKeys
              .sort((a, b) => Number(a) - Number(b))
              .map(key => img[key])
              .filter(url => typeof url === 'string' && url.trim().length > 0);
          }
          
          // Check for any string values in the object
          const values = Object.values(img).filter(v => typeof v === 'string' && v.trim().length > 0);
          if (values.length > 0) {
            return values as string[];
          }
        }
        
        return [];
      };

      // Helper function to validate and return image URL
      const getValidImageUrl = (url: string): string | null => {
        if (!url || typeof url !== 'string') return null;
        
        const trimmedUrl = url.trim();
        if (trimmedUrl.length === 0) return null;
        
        // Accept various URL formats
        if (trimmedUrl.startsWith('http://') || 
            trimmedUrl.startsWith('https://') || 
            trimmedUrl.startsWith('/') ||
            trimmedUrl.startsWith('./') ||
            trimmedUrl.startsWith('../') ||
            trimmedUrl.startsWith('data:') ||
            trimmedUrl.startsWith('blob:')) {
          return trimmedUrl;
        }
        
        return null;
      };

      // Get normalized image array
      const imageArray = normalizeImageToArray(product.image);
      
      // Get first valid image URL
      if (imageArray.length > 0) {
        for (const imgUrl of imageArray) {
          const validUrl = getValidImageUrl(imgUrl);
          if (validUrl) {
            return validUrl;
          }
        }
      }
      
    } catch (error) {
      console.error('Error getting product image:', error, product);
    }
    
    // Return placeholder if no valid image found
    return 'https://placehold.co/60x60/9E9E9E/white?text=Product';
  }

  // Async method to fetch product image from database if missing
  async fetchProductImageFromDB(productId: string): Promise<string | null> {
    try {
      const productRef = doc(this.firestore, 'products', productId);
      const productDoc = await getDoc(productRef);
      
      if (productDoc.exists()) {
        const productData = productDoc.data();
        if (productData['image']) {
          const img = Array.isArray(productData['image']) 
            ? productData['image'][0] 
            : productData['image'];
          if (img && typeof img === 'string' && img.trim().length > 0) {
            return img;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching product image from DB:', error);
    }
    return null;
  }

  // Handle image error
  handleImageError(event: any): void {
    if (event && event.target) {
      event.target.src = 'https://placehold.co/60x60/9E9E9E/white?text=Product';
      event.target.onerror = null; // Prevent infinite loop
    }
  }

  // Get order background color based on products
  getOrderBgColor(order: Order): Record<string, string> {
    try {
      if (!order.products || order.products.length === 0) {
        return {
          'background': 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
          'color': '#FFFFFF'
        };
      }

      // Use the first product's doseType for the background color
      const firstProduct = order.products[0];
      const doseType = firstProduct?.doseType || firstProduct?.dose_type;
      
      if (doseType) {
        return this.doseColorService.generateGradientStyle(doseType);
      }

      // Fallback if no doseType found
      return {
        'background': 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
        'color': '#FFFFFF'
      };
    } catch (error) {
      console.error('Error getting order background color:', error);
      return {
        'background': 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
        'color': '#FFFFFF'
      };
    }
  }

  // Get order light background color (for subtle backgrounds)
  getOrderLightBgColor(order: Order): Record<string, string> {
    try {
      if (!order.products || order.products.length === 0) {
        return {
          'background': '#f8f9fa',
          'color': '#212529'
        };
      }

      const firstProduct = order.products[0];
      const doseType = firstProduct?.doseType || firstProduct?.dose_type;
      
      if (doseType) {
        const lightColor = this.doseColorService.getDoseLightColor(doseType);
        const textColor = this.doseColorService.getTextOnLight(doseType);
        return {
          'background': lightColor,
          'color': textColor
        };
      }

      return {
        'background': '#f8f9fa',
        'color': '#212529'
      };
    } catch (error) {
      console.error('Error getting order light background color:', error);
      return {
        'background': '#f8f9fa',
        'color': '#212529'
      };
    }
  }

  // Parse delivery address to extract name, address, and mobile
  parseDeliveryAddress(deliveryAddress: string): { name: string; address: string; mobile: string } {
    if (!deliveryAddress) {
      return { name: 'N/A', address: 'N/A', mobile: 'N/A' };
    }

    try {
      // Format: "Name, Address, City, State - Pincode. Mobile: 1234567890"
      const mobileMatch = deliveryAddress.match(/Mobile:\s*(\d+)/i);
      const mobile = mobileMatch ? mobileMatch[1] : 'N/A';

      // Extract name and address (everything before "Mobile:")
      const beforeMobile = deliveryAddress.split(/Mobile:/i)[0].trim();
      
      // Split by comma to get name (first part) and address (rest)
      const parts = beforeMobile.split(',');
      
      if (parts.length >= 2) {
        const name = parts[0].trim();
        const address = parts.slice(1).join(',').trim();
        return { name, address, mobile };
      } else {
        // If no comma found, treat entire string as address
        return { name: 'N/A', address: beforeMobile, mobile };
      }
    } catch (error) {
      console.error('Error parsing delivery address:', error);
      return { name: 'N/A', address: deliveryAddress, mobile: 'N/A' };
    }
  }

  /**
   * Check if order can be tracked (has AWB code and is shipped)
   */
  canTrackOrder(order: Order): boolean {
    return order.status === 'shipped' && !!(order.shiprocketAwbCode || order.trackingNumber);
  }

  /**
   * Get tracking number/AWB code for an order
   */
  getTrackingNumber(order: Order): string | null {
    return order.shiprocketAwbCode || order.trackingNumber || null;
  }

  /**
   * Track shipment for an order
   */
  async trackOrder(order: Order): Promise<void> {
    const trackingNumber = this.getTrackingNumber(order);
    
    if (!trackingNumber) {
      this.toastService.error('Tracking number not available for this order.');
      return;
    }

    this.trackingLoading[order.id] = true;
    this.selectedOrderForTracking = order;

    try {
      const result = await this.shiprocketService.trackShipment(trackingNumber).toPromise();
      
      if (result?.tracking_data) {
        this.trackingData[order.id] = result.tracking_data;
        this.showTrackingModal = true;
      } else {
        this.toastService.error('No tracking information available.');
      }
    } catch (error) {
      console.error('Error tracking shipment:', error);
      this.toastService.error('Failed to fetch tracking information. Please try again.');
    } finally {
      this.trackingLoading[order.id] = false;
    }
  }

  /**
   * Close tracking modal
   */
  closeTrackingModal(): void {
    this.showTrackingModal = false;
    this.selectedOrderForTracking = null;
  }

  /**
   * Get tracking status text
   */
  getTrackingStatus(order: Order): string {
    const tracking = this.trackingData[order.id];
    if (!tracking) return '';
    return tracking.status || 'In Transit';
  }

} 